<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import type { FilterField, FilterOption, FilterRule, SearchFilterAdapter } from './types'
import { fieldError, newRule, operators } from './model'
import { retainChoiceLabels, visibleChoiceIds } from './choices'

const props = defineProps<{ modelValue: FilterRule; fields: FilterField[]; adapter: SearchFilterAdapter; validate: boolean }>()
const emit = defineEmits<{ 'update:modelValue': [FilterRule]; remove: []; labels: [string, FilterOption[]] }>()
const field = computed(() => props.fields.find(f => f.key === props.modelValue.field))
const root = ref<HTMLElement | null>(null)
const valueInput = ref<{ focus?: () => void; $el?: HTMLElement } | null>(null)
const menu = ref(false)
const options = ref<FilterOption[]>([])
const pageOptions = ref<FilterOption[]>([])
const expanded = ref<string[]>([])
const loading = ref(false)
const loadError = ref('')
const search = ref('')
const cursor = ref<string | null>(null)
let revision = 0
let fieldRevision = 0
let timer: ReturnType<typeof setTimeout> | undefined
const update = (patch: Partial<FilterRule>) => emit('update:modelValue', { ...props.modelValue, ...patch })
const multiple = computed(() => ['in', 'not_in'].includes(props.modelValue.operator))
const hasValue = computed(() => !['empty', 'filled'].includes(props.modelValue.operator))
const selected = computed(() => Array.isArray(props.modelValue.value) ? props.modelValue.value : props.modelValue.value ? [props.modelValue.value] : [])
const error = computed(() => props.validate ? fieldError(props.modelValue, props.fields) : '')
const visibleIds = computed(() => field.value?.tree
  ? visibleChoiceIds(pageOptions.value, expanded.value, !!search.value)
  : new Set(pageOptions.value.map(item => item.value)))
const valueLabel = computed(() => {
  const operator = props.modelValue.operator
  if (['in', 'eq', 'empty', 'filled'].includes(operator)) return 'Значение'
  if (operator === 'gte') return field.value?.type === 'date' ? 'Не раньше' : 'Не меньше'
  if (operator === 'lte') return field.value?.type === 'date' ? 'Не позже' : 'Не больше'
  return operators[operator] || 'Значение'
})
const choiceFilter = computed(() => field.value?.type === 'reference'
  ? { title: (_value: unknown, _query: string, item?: { value: string }) => !!item && visibleIds.value.has(item.value) }
  : undefined)
const mergeOptions = (items: FilterOption[]) => [...new Map(items.map(item => [item.value, item])).values()]

function selectField(key: string) {
  const next = props.fields.find(f => f.key === key)
  if (next) emit('update:modelValue', newRule(next))
}
function toggleBranch(value: string) {
  expanded.value = expanded.value.includes(value) ? expanded.value.filter(id => id !== value) : [...expanded.value, value]
}
function revealSelection() {
  const ancestors = options.value.filter(item => selected.value.includes(item.value)).flatMap(item => item.ancestors || [])
  expanded.value = [...new Set([...expanded.value, ...ancestors])]
}
async function load(more = false) {
  const current = field.value
  if (current?.type !== 'reference') return
  const id = ++revision
  loading.value = true
  loadError.value = ''
  try {
    const page = await props.adapter.loadChoices(current.key, search.value, more ? cursor.value : null)
    if (id !== revision) return
    pageOptions.value = more ? mergeOptions([...pageOptions.value, ...page.options]) : page.options
    options.value = retainChoiceLabels(pageOptions.value, options.value)
    cursor.value = page.cursor
    revealSelection()
    emit('labels', current.key, options.value)
  } catch {
    if (id === revision) loadError.value = 'Не удалось загрузить значения'
  } finally {
    if (id === revision) loading.value = false
  }
}
async function hydrateSelection() {
  const current = field.value
  const ids = selected.value.filter(id => !options.value.some(option => option.value === id))
  if (current?.type !== 'reference' || !ids.length) return
  const context = fieldRevision
  try {
    const page = await props.adapter.loadChoices(current.key, '', null, ids)
    if (context !== fieldRevision) return
    // A label lookup can return a node without its parents. Keep the richer tree entry.
    options.value = retainChoiceLabels(options.value, page.options)
    revealSelection()
    emit('labels', current.key, options.value)
  } catch { /* Loading the directory remains retryable. */ }
}
watch(() => field.value?.key, () => {
  revision++
  fieldRevision++
  clearTimeout(timer)
  options.value = []
  pageOptions.value = []
  expanded.value = []
  search.value = ''
  cursor.value = null
  menu.value = false
  loading.value = false
  loadError.value = ''
  load()
  hydrateSelection()
}, { immediate: true })
watch(selected, hydrateSelection)
watch(search, () => {
  if (field.value?.type !== 'reference') return
  revision++
  clearTimeout(timer)
  cursor.value = null
  pageOptions.value = []
  loading.value = true
  timer = setTimeout(() => load(), 250)
})
onBeforeUnmount(() => {
  revision++
  fieldRevision++
  clearTimeout(timer)
})
defineExpose({
  async openValue() {
    root.value?.scrollIntoView({ block: 'nearest' })
    await nextTick()
    valueInput.value?.focus?.()
    valueInput.value?.$el?.querySelector('input')?.focus()
    if (hasValue.value && field.value && ['reference', 'choice'].includes(field.value.type)) menu.value = true
  },
})
</script>

<template>
  <div ref="root" class="filter-rule">
    <VAutocomplete
      :model-value="modelValue.field"
      :items="fields"
      item-title="label"
      item-value="key"
      label="Поле"
      hide-details
      density="compact"
      class="filter-rule-field"
      @update:model-value="selectField"
    />
    <div class="filter-rule-value">
      <VAutocomplete
        v-if="hasValue && field && ['reference', 'choice'].includes(field.type)"
        ref="valueInput"
        v-model:search="search"
        v-model:menu="menu"
        :model-value="modelValue.value"
        :items="field.type === 'choice' ? field.options : options"
        item-title="label"
        item-value="value"
        :multiple="multiple"
        :chips="multiple"
        closable-chips
        :custom-key-filter="choiceFilter"
        :loading="loading"
        :label="valueLabel"
        :hint="field.hint"
        persistent-hint
        :error-messages="error || loadError"
        :hide-details="!error && !loadError && !field.hint"
        density="compact"
        :menu-props="{ maxHeight: 320, maxWidth: 'min(620px, calc(100vw - 32px))' }"
        @update:model-value="update({ value: $event })"
      >
        <template #item="{ props: itemProps, item }">
          <VListItem
            v-bind="itemProps"
            class="filter-choice-item"
            :title="field.tree ? item.raw.nodeLabel || item.title : item.title"
            :style="field.tree ? { paddingInlineStart: `${12 + Math.min(item.raw.ancestors?.length || 0, 6) * 20}px` } : undefined"
            @keydown.right.prevent="field.tree && item.raw.hasChildren && !expanded.includes(item.value) && toggleBranch(item.value)"
            @keydown.left.prevent="field.tree && expanded.includes(item.value) && toggleBranch(item.value)"
          >
            <template #prepend="{ isSelected }">
              <template v-if="field.tree">
                <VBtn
                  v-if="item.raw.hasChildren"
                  :icon="expanded.includes(item.value) || search ? 'ri-arrow-down-s-line' : 'ri-arrow-right-s-line'"
                  variant="text"
                  size="x-small"
                  :aria-label="`${expanded.includes(item.value) ? 'Свернуть' : 'Развернуть'}: ${item.raw.nodeLabel || item.title}`"
                  :aria-expanded="!!search || expanded.includes(item.value)"
                  :disabled="!!search"
                  @mousedown.prevent
                  @click.stop="toggleBranch(item.value)"
                />
                <span v-else class="filter-tree-spacer" />
              </template>
              <VCheckboxBtn
                v-if="multiple"
                :model-value="isSelected"
                tabindex="-1"
                :ripple="false"
                class="filter-choice-checkbox"
              />
            </template>
          </VListItem>
        </template>
        <template #chip="{ props: chipProps, item }">
          <VChip v-bind="chipProps" :title="item.title" class="filter-value-chip">
            <span>{{ item.title }}</span>
          </VChip>
        </template>
        <template #no-data>
          <VListItem :title="loading ? 'Загрузка…' : loadError || 'Нет подходящих значений'" />
        </template>
        <template #append-item>
          <VBtn
            v-if="cursor || loadError"
            block
            variant="text"
            :loading="loading"
            @click="load(!!cursor && !loadError)"
          >
            {{ loadError ? 'Повторить' : 'Показать ещё' }}
          </VBtn>
        </template>
      </VAutocomplete>
      <VCombobox
        v-else-if="hasValue && multiple && field?.type === 'number'"
        ref="valueInput"
        :model-value="Array.isArray(modelValue.value) ? modelValue.value : []"
        multiple
        chips
        closable-chips
        :delimiters="[',', ';', ' ']"
        label="Номера"
        :error-messages="error"
        :hide-details="!error"
        density="compact"
        @update:model-value="update({ value: $event.map(String) })"
      />
      <VTextField
        v-else-if="hasValue"
        ref="valueInput"
        :model-value="modelValue.value"
        :type="field?.type === 'date' ? 'date' : field?.type === 'number' ? 'number' : 'text'"
        :label="valueLabel"
        :active="field?.type === 'date'"
        :error-messages="error"
        :hide-details="!error"
        density="compact"
        @update:model-value="update({ value: String($event ?? '') })"
      />
      <VTextField
        v-else
        :model-value="operators[modelValue.operator]"
        label="Значение"
        readonly
        hide-details
        density="compact"
      />
    </div>
    <VBtn
      icon="ri-close-line"
      variant="text"
      size="small"
      class="filter-rule-remove"
      :aria-label="`Удалить поле ${field?.label || ''}`"
      @click="emit('remove')"
    />
  </div>
</template>

<style scoped>
.filter-rule { display: grid; grid-template-columns: minmax(0, 2fr) minmax(0, 3fr) 32px; gap: 12px; align-items: start; }
.filter-rule-field, .filter-rule-value { min-width: 0; }
.filter-rule-remove { grid-column: 3; grid-row: 1; }
.filter-rule :deep(.v-autocomplete__selection) { max-width: 100%; min-width: 0; }
.filter-rule :deep(.v-field__input) { min-width: 0; }
.filter-rule :deep(.v-autocomplete__selection-text) { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-value-chip { max-width: 100%; }
.filter-value-chip :deep(.v-chip__content) { min-width: 0; }
.filter-value-chip span { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.filter-value-chip :deep(.v-chip__close) { flex-shrink: 0; }
.filter-tree-spacer { width: 24px; flex-shrink: 0; }
.filter-choice-checkbox { pointer-events: none; margin-inline-end: 8px; }
.filter-choice-item :deep(.v-list-item-title) { white-space: normal; overflow-wrap: anywhere; }
@media (max-width: 600px) {
  .filter-rule { grid-template-columns: minmax(0, 1fr) 32px; gap: 10px; }
  .filter-rule-value { grid-column: 1; }
  .filter-rule-remove { grid-column: 2; }
}
</style>
